import {
  Contract,
  ModalDialog,
  Mtds,
  templates,
  UpdateContract,
  SaveError,
  useUpdateContractMutation,
  widgets
} from 'prove-common'
import {
  Form,
  IChangeEvent,
  RJSFSchema,
  UiSchema,
  validator
} from 'prove-common/rjsf'
import { useState } from 'react'

export const updateContractSchema: RJSFSchema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      title: 'Tittel',
      description: 'Gi kontrakten en kort og beskrivende tittel'
    },
    startDate: {
      type: 'string',
      format: 'date-time',
      title: 'Startdato',
      description: 'Når skal kontrakten starte?'
    },
    endDate: {
      type: 'string',
      format: 'date-time',
      title: 'Sluttdato',
      description: 'Når skal kontrakten slutte?'
    }
  },
  required: ['title']
}
export const updateContractUiSchema: UiSchema = {
  'ui:options': {
    allowWrap: true
  },
  title: {
    'ui:classNames': 'basis-full max-w-initial'
  }
}

type EditContractModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  contract: Contract
}

export const EditContractModal = (props: EditContractModalProps) => (
  <ContractEditor key={props.contract.id} {...props} />
)

const ContractEditor = (props: EditContractModalProps) => {
  const [sessionContract, setSessionContract] = useState<Contract | null>(null)

  // Start from the latest server version only when there is no retained draft.
  if (props.isOpen && sessionContract === null) {
    setSessionContract(props.contract)
  }

  const endSession = () => {
    props.setOpen(false)
    setSessionContract(null)
  }

  return sessionContract ? (
    <EditContractSession
      {...props}
      contract={sessionContract}
      onEndSession={endSession}
    />
  ) : null
}

const EditContractSession = ({
  isOpen,
  setOpen,
  contract,
  onEndSession
}: EditContractModalProps & { onEndSession: () => void }) => {
  const [baselineContract] = useState(contract)
  const updateContract = useUpdateContractMutation()
  const [saveError, setSaveError] = useState<unknown>()
  const [formData, setFormData] = useState<Partial<UpdateContract>>({
    title: contract.title,
    startDate: contract.startDate,
    endDate: contract.endDate
  })
  const onSubmit = ({ formData }: IChangeEvent<UpdateContract>): void => {
    if (
      formData != null &&
      baselineContract.etag != null &&
      !updateContract.isPending
    ) {
      setSaveError(undefined)
      updateContract
        .mutateAsync({
          contractId: baselineContract.id,
          etag: baselineContract.etag,
          body: {
            ownerId: baselineContract.ownerId,
            ...formData
          }
        })
        .then(onEndSession)
        .catch((error) => {
          setSaveError(error)
        })
    }
  }
  return (
    <ModalDialog
      id='edit-contract-modal'
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command='close'
        commandfor='edit-contract-modal'
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Rediger kontrakt</Mtds.Heading>
        <SaveError error={saveError} action='oppdatere kontrakten' />
        <Form
          noHtml5Validate
          schema={updateContractSchema}
          uiSchema={updateContractUiSchema}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={formData}
          onSubmit={(data) => {
            onSubmit(data)
          }}
          onChange={(data) => {
            setFormData(data.formData)
          }}
        >
          <Mtds.Flex className='mt-8'>
            <Mtds.Button
              type='button'
              onClick={onEndSession}
              disabled={updateContract.isPending}
            >
              Forkast
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              aria-busy={updateContract.isPending}
              disabled={updateContract.isPending}
            >
              Lagre
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
